const DEFAULT_PROXY_BASE_URL = "/wialon";
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

const DEFAULT_BASE_URL =
  process.env.REACT_APP_WIALON_BASE_URL ||
  (process.env.NODE_ENV === "development"
    ? DEFAULT_PROXY_BASE_URL
    : "https://hst-api.wialon.com");

const CORE_SEARCH_ITEMS_SVC = "core/search_items";
const CORE_BATCH_SVC = "core/batch";
const DEFAULT_AJAX_PATH = "/wialon/ajax.html";
const DEFAULT_AJAX_URL = `${DEFAULT_BASE_URL}${DEFAULT_AJAX_PATH}`;

const shouldPreferProxy = (candidate) => {
  if (
    typeof window === "undefined" ||
    process.env.NODE_ENV !== "development" ||
    !candidate ||
    !ABSOLUTE_URL_PATTERN.test(candidate)
  ) {
    return false;
  }

  try {
    const parsed = new URL(candidate);

    if (parsed.origin === window.location.origin) {
      return false;
    }
  } catch (error) {
    return false;
  }

  return true;
};

const resolveAjaxUrl = (baseUrl) => {
  const candidate = (() => {
    const raw = (baseUrl || DEFAULT_BASE_URL || "").trim();

    if (!raw) {
      return raw;
    }

    return shouldPreferProxy(raw) ? DEFAULT_PROXY_BASE_URL : raw;
  })();

  if (!candidate) {
    return DEFAULT_AJAX_URL;
  }

  if (/\/wialon\/ajax\.html(\?.*)?$/i.test(candidate)) {
    return candidate;
  }

  const withoutTrailingSlash = candidate.replace(/\/+$/, "");

  if (/\/wialon$/i.test(withoutTrailingSlash)) {
    return `${withoutTrailingSlash}/ajax.html`;
  }

  return `${withoutTrailingSlash}/wialon/ajax.html`;
};

const createRequestUrl = (ajaxUrl) => {
  if (!ajaxUrl) {
    throw new Error("Missing Wialon ajaxUrl");
  }

  if (ABSOLUTE_URL_PATTERN.test(ajaxUrl)) {
    return new URL(ajaxUrl);
  }

  const origin =
    (typeof window !== "undefined" && window.location && window.location.origin) ||
    undefined;

  return new URL(ajaxUrl, origin || "http://localhost");
};

const buildSvcUrl = ({ baseUrl, svc, sid }) => {
  const ajaxUrl = resolveAjaxUrl(baseUrl);
  const requestUrl = createRequestUrl(ajaxUrl);

  requestUrl.searchParams.set("svc", svc);

  if (sid) {
    requestUrl.searchParams.set("sid", sid);
  }

  return requestUrl.toString();
};

const decodeError = (data) => {
  if (!data) {
    return "Empty response from Wialon";
  }

  if (typeof data.error === "number") {
    return `Wialon error code: ${data.error}`;
  }

  if (typeof data.message === "string") {
    return data.message;
  }

  return "Unknown Wialon error";
};

const toFormBody = (params) =>
  Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(
          typeof value === "string" ? value : JSON.stringify(value)
        )}`
    )
    .join("&");

const callWialon = async ({
  svc,
  params = {},
  sid,
  baseUrl,
  signal,
  query = {},
  body = {},
}) => {
  if (!svc) {
    throw new Error("Missing Wialon service (svc) name");
  }

  const targetUrl = buildSvcUrl({ baseUrl, svc, sid });

  const requestUrl = new URL(targetUrl);

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      requestUrl.searchParams.set(key, value);
    }
  });

  const formParams = {};

  if (params && Object.keys(params).length > 0) {
    formParams.params = params;
  }

  Object.entries(body).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formParams[key] = value;
    }
  });

  const formBody = toFormBody(formParams);

  const response = await fetch(requestUrl.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: formBody,
    signal,
  });

  if (!response.ok) {
    const message = `${response.status} ${response.statusText}`;
    throw new Error(`Failed Wialon request (${svc}): ${message}`);
  }

  const data = await response.json();

  if (data && typeof data.error !== "undefined") {
    throw new Error(`Failed Wialon request (${svc}): ${decodeError(data)}`);
  }

  return data;
};

const loginWithToken = async ({ token, baseUrl, signal } = {}) => {
  const resolvedToken = token || process.env.REACT_APP_WIALON_TOKEN;

  if (!resolvedToken) {
    throw new Error("Missing Wialon access token");
  }

  return callWialon({
    svc: "token/login",
    params: { token: resolvedToken, operateAs: "", fl: 1 },
    baseUrl,
    signal,
  });
};

const logout = async ({ sid, baseUrl, signal }) => {
  if (!sid) {
    return null;
  }

  try {
    return await callWialon({ svc: "core/logout", sid, baseUrl, signal });
  } catch (error) {
    // Best-effort logout: swallow errors to avoid masking primary failures.
    console.warn("Failed to logout from Wialon", error);
    return null;
  }
};

const searchUnits = ({
  sid,
  baseUrl,
  signal,
  spec = {
    itemsType: "avl_unit",
    propName: "sys_name",
    propValueMask: "*",
    sortType: "sys_last_message",
  },
  flags = 1025,
  force = 1,
  from = 0,
  to = 0,
} = {}) =>
  callWialon({
    svc: CORE_SEARCH_ITEMS_SVC,
    sid,
    baseUrl,
    signal,
    params: { spec, flags, force, from, to },
  });

const searchResources = ({
  sid,
  baseUrl,
  signal,
  spec = {
    itemsType: "avl_resource",
    propName: "sys_name",
    propValueMask: "*",
    sortType: "sys_name",
  },
  flags = 0x0001 | 0x0002 | 0x0020,
  force = 1,
  from = 0,
  to = 0,
} = {}) =>
  callWialon({
    svc: CORE_SEARCH_ITEMS_SVC,
    sid,
    baseUrl,
    signal,
    params: { spec, flags, force, from, to },
  });

const getZoneData = ({ sid, baseUrl, signal, resourceId, itemId }) => {
  const targetItemId =
    itemId !== undefined && itemId !== null ? itemId : resourceId;

  if (typeof targetItemId === "undefined" || targetItemId === null) {
    throw new Error("Missing Wialon resource id for zone metadata request");
  }

  return callWialon({
    svc: "resource/get_zone_data",
    sid,
    baseUrl,
    signal,
    params: { itemId: targetItemId },
  });
};

const getZonesByPoint = ({
  sid,
  baseUrl,
  signal,
  lat,
  lng,
  radius = Number(process.env.REACT_APP_WIALON_ZONE_RADIUS || 400),
  flags = 1,
  resourceId,
  zoneSpec,
}) => {
  const hasZoneSpec =
    zoneSpec &&
    typeof zoneSpec === "object" &&
    zoneSpec.zoneId &&
    Object.keys(zoneSpec.zoneId).length > 0;

  const params = {};

  if (hasZoneSpec) {
    params.spec = {
      ...zoneSpec,
      lat,
      lon: lng,
    };

    if (typeof radius === "number" && !Number.isNaN(radius)) {
      params.spec.radius = radius;
    }

    if (typeof flags === "number" && !Number.isNaN(flags)) {
      params.spec.flags = flags;
    }
  } else {
    params.lat = lat;
    params.lon = lng;

    if (typeof radius === "number" && !Number.isNaN(radius)) {
      params.radius = radius;
    }

    if (typeof flags === "number" && !Number.isNaN(flags)) {
      params.flags = flags;
    }

    if (resourceId !== undefined && resourceId !== null) {
      params.res_id = resourceId;
    }
  }

  return callWialon({
    svc: "resource/get_zones_by_point",
    sid,
    baseUrl,
    signal,
    params,
  });
};

const batchRequest = async ({
  sid,
  baseUrl,
  signal,
  requests = [],
  flags,
}) => {
  if (!Array.isArray(requests) || requests.length === 0) {
    return [];
  }

  const body = {};

  if (typeof flags === "number") {
    body.flags = flags;
  }

  return callWialon({
    svc: CORE_BATCH_SVC,
    sid,
    baseUrl,
    signal,
    params: requests,
    body,
  });
};

const getUnitLastMessage = ({ sid, baseUrl, signal, unitId }) =>
  callWialon({
    svc: "unit/get_last_point",
    sid,
    baseUrl,
    signal,
    params: { unitId },
  });

const updateDataFlags = ({ sid, baseUrl, signal, itemId, flags }) =>
  callWialon({
    svc: "core/update_data_flags",
    sid,
    baseUrl,
    signal,
    params: { spec: [{ id: itemId, flags }] },
  });

export {
  batchRequest,
  callWialon,
  CORE_BATCH_SVC,
  CORE_SEARCH_ITEMS_SVC,
  DEFAULT_AJAX_URL,
  getZoneData,
  getUnitLastMessage,
  getZonesByPoint,
  loginWithToken,
  logout,
  buildSvcUrl,
  searchResources,
  searchUnits,
  updateDataFlags,
};

export default callWialon;
